import type {
  DocumentPublic,
  DocumentSearch,
  SettableVisibility,
} from "@common/client"
import { DocumentsService } from "@common/client"
import { useToast } from "@common/hooks/use-toast"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  makeAxiosMutation,
  makeGroupPages,
  makeInfiniteQuery,
  makeQuery,
  unwrap,
} from "./utils"

export const documentsBaseKey = ["documents"] as const

export const documentsListKey = (
  params: Parameters<typeof DocumentsService.searchDocuments>[0],
) => [...documentsBaseKey, "list", params] as const

export const documentsQuery = makeQuery({
  serviceCall: DocumentsService.searchDocuments,
  queryKey: documentsListKey,
})

const DOCUMENTS_PAGE_SIZE = 50

export type DocumentPagesParams = Omit<DocumentSearch, "skip" | "limit">

export const documentsInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: DocumentPagesParams, page) =>
    DocumentsService.searchDocuments({
      documentSearch: { ...params, ...page },
    }),
  queryKey: (params) => [...documentsBaseKey, "pages", params],
  pageSize: DOCUMENTS_PAGE_SIZE,
})

export const documentGroupPages = makeGroupPages({
  serviceCall: (params: DocumentPagesParams, page) =>
    DocumentsService.searchDocuments({
      documentSearch: { ...params, ...page },
    }),
  queryKey: (params) => [...documentsBaseKey, "grouped", params],
  pageSize: DOCUMENTS_PAGE_SIZE,
})

export const airworthinessDirectiveGroupPages = makeGroupPages({
  serviceCall: (params: DocumentPagesParams, page) =>
    DocumentsService.searchAirworthinessDirectives({
      documentSearch: { ...params, ...page },
    }),
  queryKey: (params) => [
    ...documentsBaseKey,
    "airworthiness-directives",
    "grouped",
    params,
  ],
  pageSize: DOCUMENTS_PAGE_SIZE,
})

export const airworthinessDirectivesInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: DocumentPagesParams, page) =>
    DocumentsService.searchAirworthinessDirectives({
      documentSearch: { ...params, ...page },
    }),
  queryKey: (params) => [
    ...documentsBaseKey,
    "airworthiness-directives",
    params,
  ],
  pageSize: DOCUMENTS_PAGE_SIZE,
})

export const documentFilterOptionsQuery = makeQuery({
  serviceCall: DocumentsService.filterOptions,
  queryKey: (params) => [...documentsBaseKey, "filter-options", params],
})

export const documentKey = (documentId: string) =>
  [...documentsBaseKey, "detail", documentId] as const

export const documentQuery = makeQuery({
  serviceCall: DocumentsService.get,
  queryKey: (params) => documentKey(params.document_id),
})

export const documentBlobKey = (documentId: string, format: string | null) =>
  [...documentsBaseKey, "blob", documentId, { format }] as const

export const documentBlobQuery = makeQuery({
  serviceCall: DocumentsService.download,
  queryKey: (params) =>
    documentBlobKey(params.document_id, params.format ?? null),
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
})

export const useDownloadDocumentMutation = makeAxiosMutation({
  serviceCall: DocumentsService.download,
  invalidates: () => [],
})

export const useUpdateDocumentVisibilityMutation = makeAxiosMutation({
  serviceCall: DocumentsService.update,
  invalidates: () => [documentsBaseKey],
})

export const useBulkUpdateDocumentVisibilityMutation = makeAxiosMutation({
  serviceCall: DocumentsService.updateMany,
  invalidates: () => [documentsBaseKey],
})

export const useArchiveDocumentMutation = makeAxiosMutation({
  serviceCall: DocumentsService.archiveDocument,
  invalidates: () => [documentsBaseKey],
})

export const useArchiveDocumentsMutation = makeAxiosMutation({
  serviceCall: DocumentsService.archiveMany,
  invalidates: () => [documentsBaseKey],
})

export const useRestoreDocumentsMutation = makeAxiosMutation({
  serviceCall: DocumentsService.restoreMany,
  invalidates: () => [documentsBaseKey],
})

export const useRestoreDocumentMutation = makeAxiosMutation({
  serviceCall: DocumentsService.restoreDocument,
  invalidates: () => [documentsBaseKey],
})

export const useDeleteDocumentMutation = makeAxiosMutation({
  serviceCall: DocumentsService.deleteDocument,
  invalidates: () => [documentsBaseKey],
})

export const useReExtractDocumentMutation = makeAxiosMutation({
  serviceCall: DocumentsService.reExtract,
  invalidates: () => [documentsBaseKey],
})

export type UploadDocumentVisibility = Extract<
  SettableVisibility,
  "user" | "tenant"
>

export type UploadDocumentInput = {
  file: File
  visibility: UploadDocumentVisibility
}

export function useUploadDocumentMutation() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation<DocumentPublic, Error, File | UploadDocumentInput>({
    mutationFn: (input) => {
      const file = input instanceof File ? input : input.file
      const visibility = input instanceof File ? undefined : input.visibility

      return DocumentsService.upload({
        bodyDocumentsUpload: {
          file,
          ...(visibility ? { visibility } : {}),
        },
      }).then(unwrap)
    },
    onSuccess: (document: DocumentPublic) => {
      if (document.upload_result === "exact_duplicate") {
        toast({
          title: "You already have this file",
        })
      }
      return queryClient.invalidateQueries({ queryKey: documentsBaseKey })
    },
  })
}
